// Standards accountability, sized to what it is: 2–8 standards per project,
// three glanceable facts each (code, class avg, skills covered). That's
// SUMMARY data, so it renders as a strip of compact chips in the top zone
// beside the KPI tiles and score spread — not as another ranked bar-list in
// the diagnosis zone (the overview already has two of those and grades don't
// attach at the standard level anyway). Clicking a chip filters the matrix
// to that standard's specific skills and jumps there.
//
// Zero backend: standards + mappings ride the review endpoint the Standards
// tab already uses (shared React Query cache); per-skill stats come from the
// dashboard payload. Joined here by specific_skill_id.
import { cn } from '../../lib/cn';
import { ScoreChip } from '../ds';

function avgOrNull(values) {
  const vs = values.filter((v) => v != null);
  if (!vs.length) return null;
  return vs.reduce((a, b) => a + b, 0) / vs.length;
}

export function buildStandardsCoverage(standards, mappings, skills) {
  const skillById = new Map(skills.map((s) => [s.specific_skill_id, s]));
  return standards
    .map((std) => {
      const mapped = mappings.filter((m) => m.standard_path === std.standard_path);
      // Only skills the dashboard knows about count — a stale mapping row
      // pointing at a skill with no curriculum row shouldn't inflate the
      // denominator.
      const skillRows = mapped
        .map((m) => skillById.get(m.specific_skill_id))
        .filter(Boolean);
      const skillIds = skillRows.map((s) => s.specific_skill_id);
      const assessed = skillRows.filter((s) => s.n_graded > 0);
      return {
        standard_path: std.standard_path,
        code: std.code || std.standard_leaf || std.standard_path,
        leaf: std.standard_leaf,
        subject: std.subject,
        skillIds,
        n_total: skillRows.length,
        n_assessed: assessed.length,
        class_avg: avgOrNull(assessed.map((s) => s.class_avg)),
      };
    })
    .sort((a, b) => {
      // Weakest first; standards with nothing graded yet last.
      const aHas = a.n_assessed > 0;
      const bHas = b.n_assessed > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (aHas && bHas && a.class_avg !== b.class_avg)
        return (a.class_avg ?? 99) - (b.class_avg ?? 99);
      return (a.code || '').localeCompare(b.code || '');
    });
}

export default function StandardsCoverage({ rows, selectedStandardPath, onSelectStandard }) {
  if (rows.length === 0) return null;

  return (
    <div
      id="co-sec-standards"
      className="ds scroll-mt-28 rounded-xl border border-border bg-card px-4 py-3 shadow-card"
    >
      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Standards coverage
        </span>
        <span className="text-[10px] text-muted-foreground/80">
          class avg · skill developmental descriptors assessed — weakest first. Click one to filter
          the matrix to its descriptors.
        </span>
      </div>

      <div className="grid items-stretch gap-1.5 sm:grid-cols-2 min-[1400px]:grid-cols-3">
        {rows.map((r) => {
          const selected = r.standard_path === selectedStandardPath;
          const fullyCovered = r.n_total > 0 && r.n_assessed === r.n_total;
          const desc = r.leaf && r.leaf !== r.code ? r.leaf : r.subject;
          return (
            <button
              key={r.standard_path}
              type="button"
              onClick={() => onSelectStandard?.(selected ? null : r)}
              title={
                `${r.n_assessed} of ${r.n_total} mapped skill developmental descriptors have grades` +
                `${fullyCovered ? '' : ' — the graded work has not shown the rest yet'}.`
              }
              className={cn(
                'flex flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                  : 'border-border hover:border-primary/40 hover:bg-secondary/40'
              )}
            >
              <span className="flex items-start gap-2">
                {/* Codes distinguish at the tail (W.5.1 vs W.5.2) — wrap,
                    never truncate. */}
                <span className="min-w-0 flex-1 break-words text-[12px] font-semibold leading-snug text-foreground">
                  {r.code}
                </span>
                <ScoreChip score={r.class_avg} />
                <span
                  className={cn(
                    'text-[11px] tabular-nums',
                    fullyCovered ? 'font-medium text-success' : 'text-muted-foreground'
                  )}
                >
                  {r.n_assessed}/{r.n_total}
                </span>
              </span>
              {desc && (
                <span className="text-[11px] leading-snug text-muted-foreground">{desc}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
